import { Card, LoadingBar, OpacityOverlay } from "@administrate/piston-ux";
import moment from "moment";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { LoadingCard } from "../../components/LoadingCard";
import { AllDatesArticleHeader } from "../../components/bookables/AllDatesArticleHeader";
import { BookableList } from "../../components/bookables/BookableList";
import { Bookable, EventFieldFilter } from "../../generated/weblinkTypes";
import { useAlreadyRegisteredEventIds } from "../../hooks/useAlreadyRegisteredEventIds";
import { useBookerIntention } from "../../hooks/useBookerIntention";
import { useWeblinkSettings } from "../../hooks/useWeblinkSettings";
import { useWebLinkClient, useWebLinkQuery } from "../../hooks/weblink";
import { useViewer } from "../../providers/ViewerProvider";
import {
  WEBLINK_BASIC_COURSES_QUERY,
  WEBLINK_BASIC_LOCATIONS_QUERY,
} from "../../queries/filters";
import {
  PRIVATE_TRAINING_COORDINATOR_QUERY,
  PRIVATE_TRAINING_SELF_QUERY,
} from "../../queries/privateTraining";
import { useParams } from "../../useHistory";
import { extractNodes } from "../../utils/extractNodes";
import { BookerIntention } from "../Order/BookingPage";
import { CatalogFilter, useCatalogFilters } from "./CatalogFilterContext";
import { CatalogPage } from "./CatalogPage";
import { buildEventFilters } from "./privateTrainingFilters";

const usePrivateEventsFetcher = (
  filters: EventFieldFilter[],
  skip: boolean,
) => {
  const [bookerIntention] = useBookerIntention();
  const [query, queryName] =
    bookerIntention === BookerIntention.Coordinating
      ? [PRIVATE_TRAINING_COORDINATOR_QUERY, "privateTrainingForCoordinator"]
      : [PRIVATE_TRAINING_SELF_QUERY, "privateTraining"];
  const [offset, setOffset] = useState(0);
  const { loading, data } = useWebLinkQuery(query, {
    variables: {
      filters,
      offset,
      first: 15,
    },
    skip,
    notifyOnNetworkStatusChange: true,
  });

  const totalRecords = data?.[queryName]?.pageInfo?.totalRecords || 0;
  const events = useMemo(
    () => extractNodes<Bookable>(data?.[queryName]?.edges ?? []),
    [data, queryName],
  );

  return {
    events,
    eventsLoading: loading,
    totalEvents: totalRecords,
    offset,
    setOffset,
  };
};

export const PrivateTraining: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { t } = useTranslation();
  const { catalogType } = useParams<{ catalogType: BookerIntention }>();
  const [bookerIntention, setBookerIntention] = useBookerIntention();
  const { loading: weblinkLoading } = useWeblinkSettings();

  useEffect(() => {
    if (bookerIntention !== catalogType) {
      setBookerIntention(catalogType);
    }
  }, [bookerIntention, catalogType, setBookerIntention]);

  const [mutationRunning, setMutationRunning] = useState(false);
  const { filters, setFilterValue } = useCatalogFilters();
  const { registeredEventIds, loading } = useAlreadyRegisteredEventIds();
  const { viewer } = useViewer();
  const viewerTimeZone = viewer?.timeZoneName ?? moment.tz.guess();
  const { events, eventsLoading, totalEvents, offset, setOffset } =
    usePrivateEventsFetcher(
      buildEventFilters(
        filters,
        viewerTimeZone,
        registeredEventIds,
        bookerIntention,
      ),
      loading,
    );

  const onFilterChange = useCallback(
    (key: keyof CatalogFilter, value: unknown) => {
      setFilterValue(key, value);
      // Set our offsets back to 0 on the chance we're at an offset
      // that's no longer available due to our filters
      setOffset(0);
    },
    [setFilterValue, setOffset],
  );

  const client = useWebLinkClient();

  const locationOptions = (inputValue: string) =>
    client
      .query({
        query: WEBLINK_BASIC_LOCATIONS_QUERY,
        variables: {
          filters: [
            {
              field: "name",
              operation: "like",
              value: `%${inputValue}%`,
            },
          ],
        },
      })
      .then((result) => extractNodes(result.data.locations.edges));

  const courseOptions = (inputValue: string) =>
    client
      .query({
        query: WEBLINK_BASIC_COURSES_QUERY,
        variables: {
          filters: [
            {
              field: "name",
              operation: "like",
              value: `%${inputValue}%`,
            },
          ],
        },
      })
      .then((result) => extractNodes(result.data.courses.edges));

  return (
    <OpacityOverlay on={!!mutationRunning}>
      <CatalogPage tab="private" title={t("privateTraining")}>
        {weblinkLoading ? (
          <LoadingCard />
        ) : (
          <>
            <AllDatesArticleHeader
              onFilterChange={onFilterChange}
              loading={eventsLoading}
              locationOptions={locationOptions}
              courseOptions={courseOptions}
              showTypeFilter={false}
            />
            <Card>
              {mutationRunning && <LoadingBar isLoading />}
              <BookableList
                bookables={events}
                loading={eventsLoading}
                totalRecords={totalEvents}
                offset={offset}
                hidePrices
                hidePasses
                setOffset={setOffset}
                setMutationRunning={setMutationRunning}
                isPrivateTraining
              />
            </Card>
          </>
        )}
      </CatalogPage>
    </OpacityOverlay>
  );
};
